// ═══════════════════════════════════════════════
//   TILE SLIDE — sliding number puzzle (8-puzzle)
// ═══════════════════════════════════════════════
//
// Objective:   Slide numbered tiles into ascending order before the timer
//              runs out.
// Type:        puzzle
// Description: Click a tile next to the gap to slide it. GAME_SLIDER.timer
//              seconds per round. Solving rewards a flat bonus plus extra
//              pts per remaining second; on timeout the leaderboard score
//              is the count of correctly placed tiles.
// Tunables:    GAME_SLIDER in data.js.

const {S, canvas, ctx, claimGameReward, GAME_SLIDER, addCleanup, showGameOver} = window;

// Layout — recomputed each start in case grid size changes
let SLD_N      = 3;
let SLD_TILE   = 86;
let SLD_GAP    = 4;
let SLD_OX     = 0;
let SLD_OY     = 0;

// Pastel palette per tile (cycles through 8 entries — looks playful)
const SLD_COLORS = ['#f9a8d4', '#fcd34d', '#a8e063', '#7dd3fc', '#c084fc', '#fb7185', '#86efac', '#fbbf24'];

// ── Shared window state ──────────────────────────────────────────────
window.sldBoard = [];      // flat array length N*N; 0 means the empty cell
window.sldGap   = 0;       // index of empty cell
window.sldMoves = 0;
window.sldSolved = false;

function sldLayout() {
    const CFG = window.GAME_SLIDER;
    SLD_N    = CFG.grid;
    const inner = Math.min(canvas.width, canvas.height) - 32;
    SLD_TILE = Math.floor((inner - (SLD_N - 1) * SLD_GAP) / SLD_N);
    const totalW = SLD_N * SLD_TILE + (SLD_N - 1) * SLD_GAP;
    SLD_OX = Math.floor((canvas.width  - totalW) / 2);
    SLD_OY = Math.floor((canvas.height - totalW) / 2) + 4; // nudged down to clear HUD
}

function sldNeighbours(idx) {
    const n = SLD_N;
    const r = Math.floor(idx / n), c = idx % n;
    const out = [];
    if (r > 0)     out.push(idx - n);
    if (r < n - 1) out.push(idx + n);
    if (c > 0)     out.push(idx - 1);
    if (c < n - 1) out.push(idx + 1);
    return out;
}

function sldShuffle(moves) {
    // Apply random valid moves from the solved state — guarantees solvability
    let last = -1;
    for (let i = 0; i < moves; i++) {
        const ns = sldNeighbours(window.sldGap).filter(n => n !== last);
        const pick = ns[Math.floor(Math.random() * ns.length)];
        window.sldBoard[window.sldGap] = window.sldBoard[pick];
        window.sldBoard[pick] = 0;
        last = window.sldGap;
        window.sldGap = pick;
    }
}

function sldIsSolved() {
    const total = SLD_N * SLD_N;
    for (let i = 0; i < total - 1; i++) {
        if (window.sldBoard[i] !== i + 1) return false;
    }
    return window.sldBoard[total - 1] === 0;
}

// ── Game lifecycle ────────────────────────────────────────────────────

function startSlider() {
    const CFG = window.GAME_SLIDER;
    sldLayout();
    const total = SLD_N * SLD_N;
    window.sldBoard = [];
    for (let i = 1; i < total; i++) window.sldBoard.push(i);
    window.sldBoard.push(0);
    window.sldGap = total - 1;
    window.sldMoves = 0;
    window.sldSolved = false;

    sldShuffle(CFG.shuffleMoves);
    // Edge case: if shuffle happened to land back on solved, nudge once more
    if (sldIsSolved()) sldShuffle(8);

    window.gameRunning = true;
    window.gameScore   = 0;
    window.gameTimer   = CFG.timer;
    document.getElementById('mg-score').textContent = '0';
    document.getElementById('mg-timer').textContent = CFG.timer + 's';

    window.timerInterval = setInterval(() => {
        if (!window.gameRunning) return;
        window.gameTimer--;
        document.getElementById('mg-timer').textContent = window.gameTimer + 's';
        if (window.gameTimer <= 0) endSlider(false);
    }, 1000);
    addCleanup(() => clearInterval(window.timerInterval));

    canvas.addEventListener('click', sldClick);
    addCleanup(() => canvas.removeEventListener('click', sldClick));
    addCleanup(() => { window.sldBoard = []; window.sldGap = 0; window.sldMoves = 0; window.sldSolved = false; });
    sldDraw();
}

function sldClick(e) {
    if (!window.gameRunning || S.currentGame !== 'slider') return;
    const {x: mx, y: my} = canvasPos(e);

    for (let i = 0; i < window.sldBoard.length; i++) {
        if (window.sldBoard[i] === 0) continue;
        const tx = SLD_OX + (i % SLD_N) * (SLD_TILE + SLD_GAP);
        const ty = SLD_OY + Math.floor(i / SLD_N) * (SLD_TILE + SLD_GAP);
        if (mx < tx || mx > tx + SLD_TILE || my < ty || my > ty + SLD_TILE) continue;
        // Only adjacent-to-gap tiles can move
        if (!sldNeighbours(window.sldGap).includes(i)) {
            SFX.play('error');
            return;
        }
        window.sldBoard[window.sldGap] = window.sldBoard[i];
        window.sldBoard[i] = 0;
        window.sldGap = i;
        window.sldMoves++;
        SFX.play('click');
        sldDraw();

        if (sldIsSolved()) {
            window.sldSolved = true;
            window.gameScore = window.sldMoves; // shown for context, but reward is solve-based
            document.getElementById('mg-score').textContent = window.sldMoves;
            endSlider(true);
        }
        return;
    }
}

function endSlider(win) {
    window.gameRunning = false;
    const CFG = window.GAME_SLIDER;
    SFX.play(win ? 'win' : 'gameover');

    let p = 0, msg;
    if (win) {
        const bonus = Math.floor(Math.max(0, window.gameTimer) * CFG.rewardTimeBonus);
        p = Math.min(CFG.rewardMax, CFG.rewardSolve + bonus);
        msg = `SOLVED in ${window.sldMoves} moves!\n` + claimGameReward('slider', window.sldMoves, p);
    } else {
        // Time ran out — count tiles in correct position as score for the leaderboard
        let correct = 0;
        for (let i = 0; i < window.sldBoard.length - 1; i++) {
            if (window.sldBoard[i] === i + 1) correct++;
        }
        window.gameScore = correct;
        document.getElementById('mg-score').textContent = correct;
        msg = `Time's up! Tiles placed: ${correct}\n` + claimGameReward('slider', correct, 0);
    }
    showGameOver(msg);
}

// ── Render ───────────────────────────────────────────────────────────

function sldDraw() {
    const W = canvas.width, H = canvas.height;
    // background
    ctx.fillStyle = '#1a0010';
    ctx.fillRect(0, 0, W, H);

    // soft glow board frame
    const padX = 6, padY = 6;
    const totalW = SLD_N * SLD_TILE + (SLD_N - 1) * SLD_GAP;
    ctx.fillStyle = 'rgba(247,37,133,0.10)';
    ctx.beginPath();
    ctx.roundRect(SLD_OX - padX, SLD_OY - padY, totalW + padX * 2, totalW + padY * 2, 10);
    ctx.fill();

    // tiles
    for (let i = 0; i < window.sldBoard.length; i++) {
        const v = window.sldBoard[i];
        if (v === 0) continue;
        const tx = SLD_OX + (i % SLD_N) * (SLD_TILE + SLD_GAP);
        const ty = SLD_OY + Math.floor(i / SLD_N) * (SLD_TILE + SLD_GAP);
        const correct = v === i + 1;

        // shadow
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.beginPath();
        ctx.roundRect(tx + 3, ty + 3, SLD_TILE, SLD_TILE, 8);
        ctx.fill();

        // body
        ctx.fillStyle = SLD_COLORS[(v - 1) % SLD_COLORS.length];
        ctx.beginPath();
        ctx.roundRect(tx, ty, SLD_TILE, SLD_TILE, 8);
        ctx.fill();

        // green outline if in correct slot
        ctx.strokeStyle = correct ? '#1a0010' : 'rgba(255,255,255,0.55)';
        ctx.lineWidth   = correct ? 3 : 2;
        ctx.stroke();

        // top highlight
        ctx.fillStyle = 'rgba(255,255,255,0.35)';
        ctx.beginPath();
        ctx.roundRect(tx + 6, ty + 5, SLD_TILE - 12, 8, 3);
        ctx.fill();

        // number
        ctx.fillStyle = '#1a0010';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `bold ${Math.floor(SLD_TILE * 0.42)}px "Press Start 2P", monospace`;
        ctx.fillText(String(v), tx + SLD_TILE / 2, ty + SLD_TILE / 2 + 2);
    }

    // hint
    ctx.font      = '6px "Press Start 2P", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(247,37,133,0.55)';
    ctx.fillText('CLICK a tile next to the gap to slide!', W / 2, H - 6);
}

// ── Expose ───────────────────────────────────────────────────────────
window.startSlider = startSlider;
window.endSlider   = endSlider;
window.sldClick    = sldClick;
window.sldDraw     = sldDraw;
